<!-- InsuranceModal.vue -->
<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70 backdrop-blur-sm animate-modalFade"
  >
    <!-- Policy Card -->
    <div
      class="w-full max-w-md mx-3 bg-gray-800 text-gray-200 rounded-lg shadow-2xl border border-gray-700 animate-scaleIn overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-labelledby="policy-title"
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-gray-800">
        <div class="flex items-center gap-2">
          <i class="fas fa-shield-alt text-indigo-300 text-xl"></i>
          <h2 id="policy-title" class="text-base sm:text-lg font-semibold text-indigo-300">
            ใบซื้อประกัน
          </h2>
          <span class="text-xs px-2 py-0.5 rounded bg-gray-700 text-gray-300 border border-gray-600">
            ตึก {{ building }}
          </span>
        </div>
        <button
          @click="closeModal"
          class="text-gray-300 hover:text-indigo-300 transition-colors duration-200"
          :disabled="loading || loadingBuy"
          aria-label="ปิดหน้าต่าง"
        >
          <i class="fas fa-times text-sm"></i>
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center py-10">
        <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-indigo-600"></div>
      </div>

      <!-- Content -->
      <div v-else class="p-4 space-y-4">
        <!-- Realtime flash when protection just activated -->
        <transition name="fade">
          <div
            v-if="statusFlash"
            class="rounded-md border border-green-600 bg-green-900 bg-opacity-20 text-green-200 text-sm px-3 py-2 text-center"
          >
            <i class="fas fa-check-circle mr-1"></i> คุณได้รับการคุ้มครองแล้ว
          </div>
        </transition>

        <!-- Eligibility -->
        <div
          v-if="roomCount < 5 || isActive || weeklyPurchases >= maxWeeklyPurchases"
          class="rounded-md border border-red-500 bg-gray-900 p-3 text-red-300 text-sm text-center"
        >
          <template v-if="roomCount < 5">
            จำนวนห้องของคุณต่ำกว่า <span class="font-semibold">5 ห้อง</span> ไม่สามารถซื้อประกันได้
          </template>
          <template v-else-if="isActive">
            คุณมีประกันคุ้มครองอยู่แล้ว ไม่สามารถซื้อซ้ำได้
          </template>
          <template v-else-if="weeklyPurchases >= maxWeeklyPurchases">
            <div class="space-y-1">
              <p>คุณซื้อประกันครบ <span class="font-semibold">{{ maxWeeklyPurchases }} ครั้ง</span> ในสัปดาห์นี้แล้ว</p>
              <p class="text-xs text-gray-400">จะรีเซ็ตให้ซื้อได้ใหม่ทุกวันอาทิตย์ เวลา 21:00 น.</p>
            </div>
          </template>
        </div>

        <template v-else>
          <!-- Status / Badge -->
          <div class="flex items-center justify-between">
            <span
              class="inline-flex items-center gap-2 text-xs px-2 py-1 rounded-md border"
              :class="isActive ? 'text-green-200 border-green-600 bg-gray-900' : 'text-yellow-200 border-yellow-600 bg-gray-900'"
            >
              <i :class="isActive ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'"></i>
              {{ isActive ? 'คุ้มครองอยู่' : 'ยังไม่คุ้มครอง' }}
            </span>
            <span class="text-xs text-gray-300">จำนวนห้อง: <b class="text-indigo-300">{{ roomCount }}</b></span>
          </div>

          <!-- Current policy info -->
          <div class="bg-gray-900 border border-gray-700 rounded-md p-3">
            <div class="flex items-start gap-3">
              <i class="fas fa-file-medical text-indigo-300 mt-0.5"></i>
              <div class="flex-1">
                <div class="text-sm font-medium text-indigo-200">รายละเอียดความคุ้มครอง (อายุประกัน 24 ชั่วโมง)</div>
                <ul class="mt-1 text-xs text-gray-300 space-y-1 list-none">
                  <li class="flex gap-2"><i class="fas fa-check text-green-400 mt-0.5"></i><span><b>ไม่สูญเสียไอเทม</b> ในตัว</span></li>
                  <li class="flex gap-2"><i class="fas fa-check text-green-400 mt-0.5"></i><span><b>ไม่สูญเสียลอยสัก</b></span></li>
                  <li class="flex gap-2"><i class="fas fa-minus text-yellow-400 mt-0.5"></i><span><b>ยังคงสูญเสียห้อง เพชรและทองตามปกติ</b></span></li>
                  <li class="flex gap-2"><i class="fas fa-minus text-yellow-400 mt-0.5"></i><span><b>เมื่อตายแล้วประกันจะหมดอายุแล้วต้องซื้อใหม่ทันที</b></span></li>
                </ul>
                <div v-if="insuranceExpiry" class="mt-2 text-xs text-gray-400">
                  ประกันปัจจุบันหมดอายุ:
                  <span class="text-gray-200">{{ formatDate(insuranceExpiry) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Weekly Limit Info -->
          <div class="text-xs text-gray-300 text-center">
            คุณซื้อได้อีก <b class="text-indigo-300">{{ remainingPurchases }}</b> ครั้งในสัปดาห์นี้
          </div>

          <!-- Building 2 Price Info -->
          <div v-if="isBuilding2" class="bg-gray-900 border border-gray-700 rounded-md p-3 text-xs text-gray-300">
            <div class="font-medium text-indigo-200 mb-1">ราคาประกันตึก 2</div>
            <ul class="space-y-1">
              <li :class="weeklyPurchases === 0 ? 'text-green-300 font-semibold' : ''">ครั้งที่ 1: 300 เพชร</li>
              <li :class="weeklyPurchases === 1 ? 'text-green-300 font-semibold' : ''">ครั้งที่ 2: 20 แคช</li>
              <li :class="weeklyPurchases === 2 ? 'text-green-300 font-semibold' : ''">ครั้งที่ 3: 30 แคช</li>
            </ul>
          </div>

          <!-- Price / Pay method -->
          <div v-if="!isBuilding2" class="grid grid-cols-2 gap-2">
            <button
              @click="paymentType = 'diamond'"
              :class="[
                'group rounded-md border px-3 py-2 text-left transition-colors',
                paymentType === 'diamond' ? 'border-indigo-600 bg-gray-900' : 'border-gray-700 bg-gray-800 hover:bg-gray-700'
              ]"
            >
              <div class="flex items-center justify-between">
                <span class="text-xs text-indigo-200 flex items-center gap-1">
                  <i class="fas fa-gem"></i> ชำระด้วยเพชร
                </span>
                <i v-if="paymentType === 'diamond'" class="fas fa-check text-indigo-300 text-xs"></i>
              </div>
              <div class="mt-1 text-sm text-gray-200 font-semibold">
                {{ prices.diamond }} <span class="text-xs font-normal text-gray-400">เพชร</span>
              </div>
            </button>

            <button
              @click="paymentType = 'cash'"
              :class="[
                'group rounded-md border px-3 py-2 text-left transition-colors',
                paymentType === 'cash' ? 'border-indigo-600 bg-gray-900' : 'border-gray-700 bg-gray-800 hover:bg-gray-700'
              ]"
            >
              <div class="flex items-center justify-between">
                <span class="text-xs text-indigo-200 flex items-center gap-1">
                  <i class="fas fa-coins"></i> ชำระด้วยเงินสด
                </span>
                <i v-if="paymentType === 'cash'" class="fas fa-check text-indigo-300 text-xs"></i>
              </div>
              <div class="mt-1 text-sm text-gray-200 font-semibold">
                {{ prices.cash }} <span class="text-xs font-normal text-gray-400">แคช</span>
              </div>
            </button>
          </div>

          <!-- Building 2 fixed price display -->
          <div v-else class="bg-gray-900 border border-indigo-600 rounded-md p-3 text-center">
            <div class="text-xs text-gray-400">ครั้งที่ {{ weeklyPurchases + 1 }} ตึก 2</div>
            <div class="text-lg font-semibold text-indigo-200">
              {{ nextBuilding2Price.amount }} {{ nextBuilding2Price.currency }}
            </div>
            <div class="text-xs text-gray-400 mt-1">ชำระด้วย {{ nextBuilding2Price.currency }} เท่านั้น</div>
          </div>

          <!-- CTA: 2-step confirm -->
          <div class="mt-1">
            <button
              v-if="!confirming"
              @click="startConfirm"
              :disabled="!canAfford || loadingBuy"
              class="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 rounded-md transition-all duration-200 disabled:opacity-50"
            >
              ซื้อประกัน {{ paymentTypeLabel }}
            </button>

            <div v-else class="flex gap-2">
              <button
                @click="buyInsurance"
                :disabled="loadingBuy"
                class="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 rounded-md transition-all duration-200 disabled:opacity-50"
              >
                <span v-if="!loadingBuy">ยืนยันซื้อ</span>
                <span v-else class="inline-flex items-center gap-2"><i class="fas fa-spinner fa-spin"></i> กำลังซื้อ...</span>
              </button>
              <button
                @click="confirming = false"
                :disabled="loadingBuy"
                class="px-3 py-2 rounded-md border border-gray-600 text-gray-300 hover:bg-gray-700 transition-colors"
              >
                ยกเลิก
              </button>
            </div>

            <p v-if="!canAfford" class="text-red-300 text-xs text-center mt-2">
              ยอดเงินไม่พอสำหรับวิธีชำระนี้
            </p>
          </div>

          <!-- Footnote -->
          <div class="text-xs leading-relaxed text-gray-400 border-t border-gray-700 pt-3">
            เงื่อนไข: ความคุ้มครองมีผลทันทีหลังซื้อ และสิ้นสุดอัตโนมัติภายใน 24 ชั่วโมงนับจากเวลาซื้อ •
            ประกันนี้ไม่คุ้มครองการสูญเสียเพชร/ทอง • ซื้อได้เมื่อมีจำนวนห้องตั้งแต่ 5 ห้องขึ้นไป •
            การชำระเงินไม่สามารถขอคืนได้ • จำกัดซื้อ {{ maxWeeklyPurchases }} ครั้งต่อสัปดาห์ (รีเซ็ตทุกวันอาทิตย์ 21:00 น.)
            <span v-if="isBuilding2"> • ตึก 2 ครั้งที่ 1 จ่ายเพชร ครั้งที่ 2–3 จ่ายแคช</span>
          </div>
        </template>

        <!-- Error -->
        <div v-if="errorMessage" class="text-xs text-red-300 text-center bg-gray-900 border border-red-600 rounded-md py-2 px-3">
          {{ errorMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';

export default {
  name: 'InsuranceModal',
  props: { isOpen: { type: Boolean, default: false } },
  emits: ['close'],
  setup(props, { emit }) {
    const { user } = getUser();
    const playerStore = usePlayerStore();

    // state
    const roomCount = computed(() => playerStore.characterData?.room || 0);
    const building = computed(() => Number(playerStore.characterData?.building) || 1);
    const diamond = computed(() => playerStore.characterData?.diamond || 0);
    const cash = computed(() => playerStore.userData?.cash || 0);
    const insuranceExpiry = computed(() => playerStore.characterPrivateData?.insuranceExpiry || null);
    const weeklyPurchases = computed(() => playerStore.characterPrivateData?.weeklyInsurancePurchases || 0);
    const maxWeeklyPurchases = 3;
    const remainingPurchases = computed(() => Math.max(0, maxWeeklyPurchases - weeklyPurchases.value));
    const isBuilding2 = computed(() => building.value === 2);

    // Debug: log building detection
    watch(building, (val) => {
      console.log('[InsuranceModal] detected building:', val, 'raw:', playerStore.characterData?.building);
    }, { immediate: true });

    const loading = ref(true);
    const loadingBuy = ref(false);
    const errorMessage = ref('');

    const paymentType = ref('diamond'); // 'diamond' | 'cash'
    const confirming = ref(false);

    const wasActive = ref(false);
    const statusFlash = ref(false);
    let flashTimer = null;

    // Pricing
    const prices = computed(() => {
      const r = roomCount.value;
      return {
        diamond: Math.max(0, 10 * r),
        cash: Math.max(0, 5 * (r - 3))
      };
    });

    const nextBuilding2Price = computed(() => {
      if (weeklyPurchases.value === 0) return { amount: 300, currency: 'เพชร', type: 'diamond' };
      if (weeklyPurchases.value === 1) return { amount: 20, currency: 'แคช', type: 'cash' };
      if (weeklyPurchases.value === 2) return { amount: 30, currency: 'แคช', type: 'cash' };
      return { amount: 0, currency: '', type: '' };
    });

    const canAfford = computed(() => {
      if (isBuilding2.value) {
        const next = nextBuilding2Price.value;
        if (!next.type) return false;
        return next.type === 'diamond'
          ? diamond.value >= next.amount
          : cash.value >= next.amount;
      }
      return paymentType.value === 'diamond'
        ? diamond.value >= prices.value.diamond
        : cash.value >= prices.value.cash;
    });

    const isActive = computed(() => {
      if (!insuranceExpiry?.value) return false;
      const exp = insuranceExpiry.value.toDate ? insuranceExpiry.value.toDate() : new Date(insuranceExpiry.value);
      return exp.getTime() > Date.now();
    });

    const paymentTypeLabel = computed(() => {
      if (isBuilding2.value) {
        const next = nextBuilding2Price.value;
        return next.currency ? `(${next.currency} ${next.amount})` : '';
      }
      return paymentType.value === 'diamond'
        ? `(เพชร ${prices.value.diamond})`
        : `(เงินสด ${prices.value.cash})`;
    });

    const formatDate = (timestamp) => {
      if (!timestamp) return '';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('th-TH', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    };

    const formatNumber = (n) =>
      new Intl.NumberFormat('th-TH', { maximumFractionDigits: 0 }).format(n || 0);

    watch(insuranceExpiry, (val) => {
      const nowActive = (() => {
        if (!val) return false;
        const exp = val.toDate ? val.toDate() : new Date(val);
        return exp.getTime() > Date.now();
      })();
      if (!wasActive.value && nowActive) {
        statusFlash.value = true;
        clearTimeout(flashTimer);
        flashTimer = setTimeout(() => (statusFlash.value = false), 3000);
      }
      wasActive.value = nowActive;
    }, { immediate: true });

    // actions
    const startConfirm = () => {
      if (!canAfford.value || loadingBuy.value) return;
      confirming.value = true;
    };

    const buyInsurance = async () => {
      if (!canAfford.value || loadingBuy.value || roomCount.value < 5 || isActive.value || weeklyPurchases.value >= maxWeeklyPurchases) return;
      if (!user.value) {
        errorMessage.value = 'กรุณาล็อกอินก่อน';
        return;
      }
      loadingBuy.value = true;
      errorMessage.value = '';

      const characterActions = projectFunctions.httpsCallable('gameActions');
      try {
        const finalPaymentType = isBuilding2.value ? nextBuilding2Price.value.type : paymentType.value;
        const result = await characterActions({ action: 'characterActions', subAction: 'buyInsurance', paymentType: finalPaymentType });
        console.log(result?.data?.message || 'ซื้อประกันสำเร็จ');

        // ไม่ต้อง fetch ซ้ำ — รอ realtime อัปเดต แล้วจะแสดง flash ให้อัตโนมัติ
        confirming.value = false;
      } catch (err) {
        errorMessage.value = 'การซื้อล้มเหลว: ' + (err.message || 'เกิดข้อผิดพลาด');
      } finally {
        loadingBuy.value = false;
      }
    };

    const closeModal = () => {
      if (!loading.value && !loadingBuy.value) {
        paymentType.value = 'diamond';
        errorMessage.value = '';
        confirming.value = false;
        statusFlash.value = false;
        clearTimeout(flashTimer);
        emit('close');
      }
    };

    // lifecycle
    watch(() => props.isOpen, (open) => {
      if (open) {
        loading.value = false;
      }
    });

    onMounted(() => {
      if (props.isOpen) {
        loading.value = false;
      }
    });

    onUnmounted(() => {
      clearTimeout(flashTimer);
    });

    return {
      // state
      roomCount, building, isBuilding2, diamond, cash, insuranceExpiry, weeklyPurchases,
      loading, loadingBuy, errorMessage, paymentType, confirming,
      statusFlash,
      // computed / helpers
      prices, nextBuilding2Price, remainingPurchases, canAfford, isActive, paymentTypeLabel,
      formatDate, formatNumber,
      // actions
      startConfirm, buyInsurance, closeModal
    };
  }
};
</script>

<style scoped>
/* Animations (Tailwind v2-compatible custom) */
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.25s ease-out; }
@keyframes scaleIn { from { transform: scale(0.98); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.25s ease-out; }

/* Simple fade for flash message */
.fade-enter-active, .fade-leave-active { transition: opacity .25s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* Button micro-interaction */
button:not(:disabled):active { transform: scale(0.98); }
</style>